import type { ViewArea } from '@hibro/shared';

/**
 * Where an "open this view" request is presented (架构方案 §5.7 视图多实例).
 *
 * - `auxiliaryTab` — a side-panel instance tab (AI sessions).
 * - `editorTab` — a document-like instance tab in the editor (见 20 二期).
 * - `activeEditorView` — the single active editor view, the pre-instance behaviour.
 */
export type OpenViewRoute = 'auxiliaryTab' | 'editorTab' | 'activeEditorView';

/**
 * Decide the presentation for an open request, from the target view's declared
 * `area` plus whether the request addresses a **specific instance**.
 *
 * The `instanceKey` test is the load-bearing part, and the reason this is a named
 * function rather than two inline branches: it is what keeps the activityBar
 * behaving exactly as before. A rail `openView` action names a view and no
 * instance, so clicking IM then AI still *replaces* the active editor view — it
 * cannot accumulate tabs. Only a request that identifies one document (a routed
 * link, 见 20, or a command returning an `OpenViewRequest` with a key) opens a tab.
 *
 * `area` undefined (an unknown/uncontributed view) falls to the editor, matching
 * the previous default. Takes the declared {@link ViewArea} — which includes
 * `overlay` — rather than a `LayoutArea`, so callers can pass a contributed
 * view's own `area` without narrowing it first; an overlay view is hosted by the
 * `OverlayHost` and never reaches a tab.
 */
export function resolveOpenViewRoute(
  area: ViewArea | undefined,
  instanceKey: string | undefined,
): OpenViewRoute {
  if (area === 'auxiliaryBar') {
    return 'auxiliaryTab';
  }
  if (area === 'editor' && instanceKey !== undefined) {
    return 'editorTab';
  }
  return 'activeEditorView';
}
